import { Link, useParams, Navigate } from "react-router-dom";
import { useEffect } from "react";
import { ArrowRight, CheckCircle2, MapPin, Truck, Thermometer, Scale, ShieldCheck, FileText, Pill, Award, Lock, Stethoscope } from "lucide-react";
import SeoPageLayout from "@/components/SeoPageLayout";
import SeoEntitySummary from "@/components/SeoEntitySummary";
import { STATES } from "@/data/seoStates";

/**
 * Programmatic SEO landing page for /glp1-telehealth/:state.
 *
 * Reads state-specific copy from /data/seoStates.js and renders a
 * long-form (~1000 word) page with:
 *   - state hero
 *   - shipping + climate narrative
 *   - city list (local SEO surface)
 *   - legal / telemedicine note (avoids legal review concerns)
 *   - state-specific FAQ
 *   - JSON-LD Service + FAQPage schema for rich-results
 *
 * Pages are intentionally orphan (no nav link); discoverability is
 * via /sitemap.xml and direct AI/search referrals.
 */
export default function StateLandingPage() {
  const { state: slug } = useParams();
  const data = STATES[slug];

  useEffect(() => { /* testimonials removed for LegitScript compliance */ }, [data]);

  // Florida-only launch — any other state slug redirects to /florida.
  if (!data) return <Navigate to="/glp1-telehealth/florida" replace />;

  const canonical = `https://glpflo.org/glp1-telehealth/${data.slug}`;
  const title = `GLP-1 Telehealth in ${data.name} | Compounded Semaglutide & Tirzepatide | GLPFlo`;
  const description = `Physician-prescribed compounded semaglutide and tirzepatide delivered to ${data.name}. ${data.transit} cold-chain shipping from Florida. Flat monthly pricing, no insurance required.`;

  const faqs = [
    {
      q: `Is GLP-1 telehealth legal in ${data.name}?`,
      a: data.legalNote,
    },
    {
      q: `How quickly will my GLP-1 medication arrive in ${data.name}?`,
      a: `Most ${data.name} orders ship ${data.transit} from our temperature-monitored facility in Florida. ${data.transitNote}`,
    },
    {
      q: `Is it safe if my vial arrives warm in ${data.name}?`,
      a: `Yes. ${data.climateNote} See our peer-reviewed shipping stability study at glpflo.org/shipping-study for full citations.`,
    },
    {
      q: `Do I need insurance to use GLPFlo in ${data.name}?`,
      a: "No. GLPFlo is a cash-pay direct-to-patient service. The flat monthly subscription is the same in every state we serve — no insurance is filed, and there are no surprise fees.",
    },
    {
      q: `Which GLP-1 medications can I get in ${data.name}?`,
      a: `${data.name} residents can be prescribed compounded semaglutide or compounded tirzepatide after a U.S.-licensed prescriber asynchronously reviews their medical intake and secure-message follow-ups. Both are weekly subcutaneous injections supplied by a 503A compounding pharmacy.`,
    },
  ];

  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://glpflo.org/" },
          { "@type": "ListItem", "position": 2, "name": data.name, "item": canonical },
        ],
      },
      {
        "@type": "MedicalWebPage",
        "name": `GLP-1 Telehealth in ${data.name}`,
        "url": canonical,
        "description": description,
        "audience": { "@type": "MedicalAudience", "audienceType": "Patient" },
        "lastReviewed": "2026-02-01",
        "specialty": { "@type": "MedicalSpecialty", "name": "Endocrine" },
        "provider": {
          "@type": "MedicalOrganization",
          "name": "GLPFlo",
          "url": "https://glpflo.org/",
        },
      },
      {
        "@type": "Service",
        "serviceType": "GLP-1 Telehealth",
        "name": `GLPFlo GLP-1 Telehealth — ${data.name}`,
        "areaServed": { "@type": "State", "name": data.name },
        "provider": { "@type": "MedicalOrganization", "name": "GLPFlo", "url": "https://glpflo.org/" },
        "offers": { "@type": "Offer", "priceCurrency": "USD", "url": "https://glpflo.org/pricing" },
      },
      {
        "@type": "FAQPage",
        "mainEntity": faqs.map(f => ({
          "@type": "Question",
          "name": f.q,
          "acceptedAnswer": { "@type": "Answer", "text": f.a },
        })),
      },
    ],
  };

  return (
    <SeoPageLayout seo={{ title, description, canonical, jsonLd }}>
      {/* Hidden LLM-friendly fact block. Sits at the very top of the DOM
          (before hero) so AI crawlers hit the definitive facts before any
          marketing prose. Screen-reader accessible; visually hidden. */}
      <SeoEntitySummary
        heading={`GLPFlo in ${data.name} — Quick facts`}
        entries={[
          { label: "Service", value: "Compounded GLP-1 telehealth" },
          { label: "Provider", value: "GLPFlo (MedicalOrganization)" },
          { label: "State served", value: data.name },
          { label: "State abbreviation", value: data.abbr },
          { label: "Population served", value: data.population },
          { label: "Shipping transit", value: data.transit },
          { label: "Medications available", value: "Compounded semaglutide, compounded tirzepatide" },
          { label: "Starting monthly price (USD)", value: "$162 (semaglutide) · $199 (tirzepatide)" },
          { label: "Insurance", value: "Not required — cash-pay" },
          { label: "Provider consult required", value: "Yes — 100% telehealth" },
          { label: "Prescription type", value: "503A / 503B compounded (US-licensed pharmacy)" },
          { label: "Pharmacy partner", value: "Polomar Specialty Pharmacy (US-licensed)" },
          { label: "In-office visit required", value: "No" },
          { label: "Age eligibility", value: "18+ US residents" },
        ]}
      />
      {/* HERO */}
      <section className="bg-gradient-to-br from-purple-50 to-white border-b border-slate-100">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20">
          <div className="inline-flex items-center gap-2 text-purple-700 bg-purple-100 rounded-full px-3 py-1 text-xs font-semibold mb-4">
            <MapPin className="w-3.5 h-3.5" /> {data.name} ({data.abbr})
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 leading-tight mb-5" style={{ fontFamily: "'Outfit', sans-serif" }}>
            GLP-1 Telehealth in {data.name}
          </h1>
          <p className="text-lg sm:text-xl text-slate-700 leading-relaxed max-w-3xl">
            Doctor-prescribed compounded semaglutide and tirzepatide, shipped {data.transit} to your door. Flat monthly pricing, no insurance billing, no in-office visit required. Serving roughly {data.population}.
          </p>
          <div className="mt-7 flex flex-wrap gap-3">
            <Link
              to="/register"
              className="inline-flex items-center gap-2 rounded-full bg-purple-600 px-6 py-3 text-white font-semibold hover:bg-purple-700 transition"
              data-testid="state-hero-cta"
            >
              Start your consult <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/pricing"
              className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white px-6 py-3 font-semibold text-slate-700 hover:bg-slate-50 transition"
            >
              See flat pricing
            </Link>
          </div>

          {/* Trust badges — above the fold credibility strip */}
          <div className="mt-8 flex flex-wrap gap-x-6 gap-y-3" data-testid="state-trust-badges">
            <div className="inline-flex items-center gap-2 text-sm text-slate-700">
              <ShieldCheck className="w-4 h-4 text-emerald-600" />
              <span className="font-semibold">HIPAA-compliant</span>
            </div>
            <div className="inline-flex items-center gap-2 text-sm text-slate-700">
              <Award className="w-4 h-4 text-purple-600" />
              <span className="font-semibold">Licensed US pharmacy</span>
            </div>
            <div className="inline-flex items-center gap-2 text-sm text-slate-700">
              <Stethoscope className="w-4 h-4 text-purple-600" />
              <span className="font-semibold">Board-certified prescribers</span>
            </div>
            <div className="inline-flex items-center gap-2 text-sm text-slate-700">
              <Lock className="w-4 h-4 text-emerald-600" />
              <span className="font-semibold">PCI-secure checkout</span>
            </div>
            <div className="inline-flex items-center gap-2 text-sm text-slate-700">
              <Truck className="w-4 h-4 text-purple-600" />
              <span className="font-semibold">Cold-chain shipping</span>
            </div>
          </div>
        </div>
      </section>

      {/* KEY MESSAGE STRIP */}
      <section className="bg-emerald-50 border-y border-emerald-100">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex items-start gap-3">
          <CheckCircle2 className="w-5 h-5 text-emerald-700 flex-shrink-0 mt-0.5" />
          <p className="text-emerald-900 font-medium leading-relaxed">{data.keyMessage}</p>
        </div>
      </section>

      {/* SHIPPING + CLIMATE NARRATIVE */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-14 grid md:grid-cols-2 gap-10">
        <div>
          <div className="flex items-center gap-2 text-purple-700 mb-3">
            <Truck className="w-5 h-5" />
            <h2 className="text-xl font-bold text-slate-900">Shipping to {data.name}</h2>
          </div>
          <p className="text-slate-700 leading-relaxed">{data.transitNote}</p>
          <p className="text-slate-700 leading-relaxed mt-3">
            Every shipment includes a cold pack inside an insulated mailer and is tracked door-to-door. Patients in major {data.name} metros receive carrier-confirmed delivery windows the night before arrival.
          </p>
        </div>
        <div>
          <div className="flex items-center gap-2 text-purple-700 mb-3">
            <Thermometer className="w-5 h-5" />
            <h2 className="text-xl font-bold text-slate-900">{data.climate} climate</h2>
          </div>
          <p className="text-slate-700 leading-relaxed">{data.climateNote}</p>
          <p className="text-slate-700 leading-relaxed mt-3">
            Read the full peer-reviewed evidence at our{" "}
            <Link to="/shipping-study" className="text-purple-700 underline font-medium">Shipping Stability Study</Link>.
          </p>
        </div>
      </section>

      {/* MAJOR CITIES (Local SEO surface) */}
      <section className="bg-slate-50 border-y border-slate-100">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <h2 className="text-2xl font-bold text-slate-900 mb-3" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Serving {data.name} patients in
          </h2>
          <p className="text-slate-600 mb-5">
            Wherever you are in {data.name}, our care team operates Eastern Time and is reachable through HIPAA-compliant messaging seven days a week. Major patient communities include:
          </p>
          <div className="flex flex-wrap gap-2">
            {data.cities.map((city) => (
              <span key={city} className="inline-flex items-center gap-1.5 rounded-full bg-white border border-slate-200 px-3 py-1.5 text-sm text-slate-700">
                <MapPin className="w-3.5 h-3.5 text-purple-600" /> {city}, {data.abbr}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-6" style={{ fontFamily: "'Outfit', sans-serif" }}>
          How GLPFlo works for {data.name} patients
        </h2>
        <ol className="space-y-5">
          {[
            { t: "Create your account & complete the medical questionnaire", d: "Roughly 8-10 minutes. We collect your weight history, medications, and a single labs upload if you have recent bloodwork." },
            { t: "Prescriber reviews your intake asynchronously", d: `A U.S.-licensed prescriber holding an active ${data.name} medical license reads your questionnaire and follows up via HIPAA-compliant secure messaging — no video call required.` },
            { t: "Pharmacy compounds and ships", d: `Our 503A pharmacy partner compounds your dose and ships ${data.transit} from Florida in a cold-chain mailer.` },
            { t: "Inject weekly, track in the free Companion App", d: "Auto-tracks your steps, sends shot reminders, and logs weight + measurements — all free. Subscribers also unlock food + macro logging and an AI wellness assistant (not medical advice — always separate from clinician review). Message the care team any time for dose adjustments." },
            { t: "Refills auto-renew on your schedule", d: "No re-purchasing every month. Pause or cancel any time, no fees." },
          ].map((step, i) => (
            <li key={step.t} className="flex gap-4">
              <span className="flex-shrink-0 w-8 h-8 rounded-full bg-purple-600 text-white font-bold grid place-items-center text-sm">{i + 1}</span>
              <div>
                <h3 className="font-semibold text-slate-900 mb-0.5">{step.t}</h3>
                <p className="text-slate-600 leading-relaxed text-sm">{step.d}</p>
              </div>
            </li>
          ))}
        </ol>
      </section>

      {/* Testimonials block removed — no patient testimonials on the
          public site until every entry is verified against a signed
          media release (LegitScript readiness). */}

      {/* LEGAL NOTE */}
      <section className="bg-slate-900 text-slate-100">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="flex items-center gap-2 mb-3 text-purple-300">
            <ShieldCheck className="w-5 h-5" />
            <h2 className="text-xl font-bold text-white">Is GLP-1 telehealth legal in {data.name}?</h2>
          </div>
          <p className="leading-relaxed text-slate-300">{data.legalNote}</p>
          <p className="leading-relaxed text-slate-300 mt-3">
            Compounded semaglutide and tirzepatide are non-controlled medications, so no DEA registration or in-person visit is required for an initial prescription. Every {data.name} prescription is documented in our HIPAA-compliant medical record.
          </p>
        </div>
      </section>

      {/* PRICING TEASE */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className="rounded-2xl border border-slate-200 bg-gradient-to-br from-white to-purple-50/40 p-8">
          <div className="flex items-center gap-2 mb-3">
            <Scale className="w-5 h-5 text-purple-700" />
            <h2 className="text-xl font-bold text-slate-900">Flat pricing, every {data.name} dose tier</h2>
          </div>
          <p className="text-slate-700 leading-relaxed mb-5">
            We charge the same monthly subscription whether you&apos;re on a 0.25 mg starter dose or a full maintenance dose. No upcharges when your prescriber titrates you up — and cold-chain shipping is always included.
          </p>
          <Link
            to="/pricing"
            className="inline-flex items-center gap-2 rounded-full bg-purple-600 px-5 py-2.5 text-white font-semibold hover:bg-purple-700 transition"
            data-testid="state-pricing-cta"
          >
            See {data.abbr} pricing <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-slate-50 border-t border-slate-100">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-6" style={{ fontFamily: "'Outfit', sans-serif" }}>
            {data.name} GLP-1 Telehealth FAQ
          </h2>
          <div className="divide-y divide-slate-200 rounded-xl border border-slate-200 bg-white">
            {faqs.map((f) => (
              <div key={f.q} className="p-5">
                <h3 className="font-semibold text-slate-900 mb-1.5">{f.q}</h3>
                <p className="text-slate-700 leading-relaxed text-sm">{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
        <Pill className="w-8 h-8 text-purple-600 mx-auto mb-3" />
        <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
          Ready to start your GLP-1 program in {data.name}?
        </h2>
        <p className="text-slate-600 max-w-2xl mx-auto mb-7 leading-relaxed">
          Create your account, complete the 8-minute medical questionnaire, and a {data.name}-licensed prescriber reviews your intake asynchronously through secure messaging. Most patients are on-program within 5 business days.
        </p>
        <div className="flex flex-wrap gap-3 justify-center">
          <Link
            to="/register"
            className="inline-flex items-center gap-2 rounded-full bg-purple-600 px-7 py-3 text-white font-semibold hover:bg-purple-700 transition"
            data-testid="state-final-cta"
          >
            Get started <ArrowRight className="w-4 h-4" />
          </Link>
          <Link
            to="/faq"
            className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white px-7 py-3 font-semibold text-slate-700 hover:bg-slate-50 transition"
          >
            <FileText className="w-4 h-4" /> Read the full FAQ
          </Link>
        </div>
      </section>
    </SeoPageLayout>
  );
}
